import { map } from "rxjs/operators";
import {
	Component,
	ElementRef,
	Inject,
	Renderer2,
	ViewChild,
	ViewContainerRef,
	DOCUMENT,
	ChangeDetectorRef,
	inject
} from "@angular/core";

import { isVerticalScrollbarClick } from "@helpers/utils";
import { Modal, ModalService } from "@core-services/modal.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { Observable } from "rxjs";

@UntilDestroy()
@Component({
	selector: "modal-collection",
	templateUrl: "./modal-collection.component.html",
	styleUrls: ["./modal-collection.component.scss"],
	standalone: false
})
export class ModalCollectionComponent {
	@ViewChild("modalContainer", { read: ViewContainerRef, static: true })
	public modalContainerRef: ViewContainerRef;

	private statsigClient = inject(StatsigClientService);

	public isGlobalBannerEnabled$: Observable<boolean> = this.statsigClient.isFeatureActive(
		FEAT_GATES.UK_FINPROM_GLOBAL_RISK_WARNING
	);

	public modal: Modal = null;

	private backdropRemovalTimeout = null;

	private backdropTapExceptions: string[] = [
		"bp-modal",
		"pac-container",
		"pac-item",
		"pac-selected",
		"pac-target-input",
		"pac-placeholder",
		"pac-item-selected",
		"cdk-overlay-container",
		"modal-holder"
	]; // Google suggestions CSS classes (bigger z-index than modal)

	public constructor(
		public modalService: ModalService,
		@Inject(DOCUMENT) private document: Document,
		private elementRef: ElementRef,
		private renderer: Renderer2,
		private cdr: ChangeDetectorRef
	) {
		// Set initial display to none
		this.renderer.setStyle(this.elementRef.nativeElement, "display", "none");

		this.modalService.modalSubject
			.pipe(
				untilDestroyed(this),
				map((modal) => {
					// Modal is null at init phase
					if (!modal) {
						this.hideModals();
						return;
					}
					// Other modal is already shown
					if (this.modal) {
						this.modal.hide();
					}
					// Build the new modal inside the container
					modal.materialize(this.modalContainerRef);
					return modal;
				})
			)
			.subscribe((modal) => {
				this.modal = modal;
				if (this.modal) {
					this.showModals();
				}
			});

		document.addEventListener(
			"mousedown",
			(event: MouseEvent & { path: Element[] }) => {
				if (this.modal && this.modal.isShowing && this.modal.options && this.modal.options.showBackdrop) {
					if (!event.path || isVerticalScrollbarClick(event) || this.modal.options.preventBackdropDismiss) {
						return;
					}
					const hasExceptionElement = event.path.some((path) => {
						if (!path.className) {
							return false;
						}
						return this.backdropTapExceptions.some((item) => path.className.includes(item));
					});
					if (!hasExceptionElement) {
						this.modal.dismiss();
					}
				}
			},
			true
		);
	}

	public showModals(): void {
		this.renderer.setStyle(this.elementRef.nativeElement, "display", "flex");
		this.document.body.classList.add("modal-open");
		if (this.modal.options && this.modal.options.showBackdrop) {
			this.document.body.className += " with-backdrop show-backdrop";
		}
		if (this.backdropRemovalTimeout) {
			clearTimeout(this.backdropRemovalTimeout);
		}

		setTimeout(() => {
			this.cdr.markForCheck();
		}, 200);
	}

	public hideModals(): void {
		this.renderer.setStyle(this.elementRef.nativeElement, "display", "none");
		this.document.body.classList.remove("show-backdrop");

		this.backdropRemovalTimeout = setTimeout(() => {
			this.document.body.classList.remove("modal-open");
			this.document.body.classList.remove("with-backdrop");
		}, 120);
	}
}
